import React, { useEffect, useState } from "react";
import { ReactQuestionFactory } from "survey-react-ui";
import { ElementFactory, ItemValue, Question, Serializer } from "survey-core";

export class PillCheckboxGroupQuestionModel extends Question {
  static type = "pillcheckboxgroup";

  private _choices: ItemValue[] = [];

  constructor(name: string) {
    super(name);
  }

  getType() {
    return PillCheckboxGroupQuestionModel.type;
  }

  get choices(): ItemValue[] {
    return this._choices;
  }

  set choices(val: ItemValue[]) {
    this._choices = val;
  }

  get visibleChoices(): ItemValue[] {
    return this.choices;
  }

  public getDefaultValue(): unknown {
    return [];
  }

  public isValueEmpty(value: unknown): boolean {
    return !Array.isArray(value) || value.length === 0;
  }
}

interface PillCheckboxGroupQuestionComponentProps {
  question: PillCheckboxGroupQuestionModel;
}

export function PillCheckboxGroupQuestionComponent({
  question,
}: PillCheckboxGroupQuestionComponentProps) {
  const currentValue = question.value;
  const valueArray: string[] = Array.isArray(currentValue)
    ? currentValue
    : currentValue
      ? [currentValue]
      : [];

  const [selectedValues, setSelectedValues] = useState<string[]>(valueArray);
  const isReadOnly = question.isReadOnly;

  useEffect(() => {
    const newValueArray: string[] = Array.isArray(question.value)
      ? question.value
      : question.value
        ? [question.value]
        : [];
    setSelectedValues(newValueArray);
  }, [question.value]);

  const handleChange = (value: string) => {
    if (isReadOnly) return;

    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    setSelectedValues(newValues);
    question.value = newValues;
  };

  return (
    <div
      className={question.cssClasses?.root}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        padding: "0",
      }}
    >
      {question.visibleChoices.map((choice: unknown) => {
        const typedChoice = choice as { value: string };
        const isSelected = selectedValues.includes(typedChoice.value);

        return (
          <label
            key={typedChoice.value}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
              padding: "2px 0",
              cursor: isReadOnly ? "default" : "pointer",
              opacity: isReadOnly ? 0.7 : 1,
              fontSize: "14px",
            }}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => handleChange(typedChoice.value)}
              disabled={isReadOnly}
              style={{
                width: "16px",
                height: "16px",
                margin: "2px 0 0 0",
                cursor: isReadOnly ? "default" : "pointer",
                accentColor: "#8f8bad",
              }}
            />
            <span>{typedChoice.value}</span>
          </label>
        );
      })}
    </div>
  );
}

export function registerPillCheckboxGroupQuestion() {
  ElementFactory.Instance.registerElement(
    "pillcheckboxgroup",
    (name: string) => new PillCheckboxGroupQuestionModel(name),
  );

  Serializer.addClass(
    "pillcheckboxgroup",
    [
      {
        name: "choices:itemvalue[]",
        category: "general",
        displayName: "Choices",
      },
    ],
    () => new PillCheckboxGroupQuestionModel(""),
    "question",
  );

  ReactQuestionFactory.Instance.registerQuestion(
    PillCheckboxGroupQuestionModel.type,
    (propsOrName: string | PillCheckboxGroupQuestionComponentProps) => {
      const props =
        typeof propsOrName === "string"
          ? { question: new PillCheckboxGroupQuestionModel(propsOrName) }
          : propsOrName;
      return React.createElement(PillCheckboxGroupQuestionComponent, props);
    },
  );
}
